//External
import React from "react";
import { Card } from "react-bootstrap";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin";

gsap.registerPlugin(ScrollToPlugin);

const formatAuthorName = (name) => {
  if (!name) return name;
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${parts.slice(0, -1).join(' ')} ${lastInitial}.`;
};

const ReviewCard = ({ row }) => {
  const commentRef = React.useRef(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [clampedHeight, setClampedHeight] = React.useState(null);

  React.useEffect(() => {
    if (isExpanded) return undefined;
    const el = commentRef.current;
    if (!el) return undefined;

    const measure = () => {
      if (!el.clientWidth) return;
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
      setClampedHeight(el.clientHeight);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [row.text, isExpanded]);

  const [isAtBottom, setIsAtBottom] = React.useState(false);

  const handleScroll = () => {
    const el = commentRef.current;
    if (!el) return;
    setIsAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
  };

  const baseStyle = { marginBottom: 0 };
  const clampStyle = {
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };
  const collapsedStyle = { ...baseStyle, ...clampStyle };
  const expandedStyle = {
    ...baseStyle,
    maxHeight: clampedHeight ? `${clampedHeight}px` : undefined,
    overflowY: 'auto',
  };

  return (
    <Card className='slider-boxes-inner testimonial_card' style={{ position: 'relative' }}>
      <Card.Body className='d-flex flex-column p-0'>
        <div className='d-flex align-items-center mb-3'>
          {row.author_photo_url && (
            <img
              src={row.author_photo_url}
              alt={row.author_name}
              className='rounded-circle object-cover'
              style={{ marginRight: '12px' }}
              width={46}
              height={46}
            />
          )}
          <div>
            <span className='d-block ff-hel-med text-dark' style={{ fontSize: '18px', marginBottom: '6px' }}>{formatAuthorName(row.author_name)}</span>
            <span className='d-block'>
              {Array.from({ length: row.rating }).map((_, i) => (
                <img key={i} src='/assets/images/star.png' alt='star' width={16} height={16} className='mr-1' />
              ))}
            </span>
          </div>
        </div>
        <div style={{ position: 'relative', marginBottom: '8px' }}>
          <p
            ref={commentRef}
            onScroll={handleScroll}
            className='font-24 text-dark'
            style={isExpanded ? expandedStyle : collapsedStyle}
          >
            <span>”</span> {row.text} <span>”</span>
          </p>
          {isExpanded && !isAtBottom && (
            <div
              aria-hidden='true'
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: '32px',
                background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 100%)',
                pointerEvents: 'none',
                transition: 'opacity 0.15s ease',
              }}
            />
          )}
        </div>
        {isOverflowing && (
          <button
            type='button'
            onClick={() => {
              if (isExpanded) {
                const el = commentRef.current;
                if (el && el.scrollTop > 0) {
                  el.scrollTo({ top: 0, behavior: 'smooth' });
                  setIsAtBottom(false);
                  window.setTimeout(() => setIsExpanded(false), 350);
                  return;
                }
                setIsExpanded(false);
                setIsAtBottom(false);
                return;
              }
              setIsExpanded(true);
            }}
            className='btn btn-link p-0 text-left'
            style={{ alignSelf: 'flex-start', fontSize: '14px', textDecoration: 'underline' }}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
        <img
          src='/assets/images/google_icon.png'
          alt='Google'
          width={24}
          height={24}
          className='google-review-icon'
        />
      </Card.Body>
    </Card>
  );
};

/*******************
@Purpose : Used for testimonial
@Parameter : {}
@Author : INIC
******************/
class GoogleReviews extends React.Component {
  constructor(props) {
    super(props);
    this.imgWidth = 734;
    this.imgHeight = 305;
    this.sliderWrapperRef = React.createRef();
    this.sliderInnerRef = React.createRef();
    this.containerRef = React.createRef();
    this.headerContainerRef = React.createRef();
    this.state = {
      timeoffset: 80,
      transformWidth: 0,
      contentWidth: 0,
      itemWidth: 0,
      showLeftButton: true,
      showRightButton: true,
      measured: false
    };
  }

/*******************
@Purpose : Used for left slider
@Parameter : {duration}
@Author : INIC
******************/
  left() {
    gsap.to(this.sliderWrapperRef.current, {
      duration: 0.4,
      scrollTo: {
        x: "-=" + this.state.itemWidth,
      },
    });
  }

/*******************
@Purpose : Used for Right slider
@Parameter : {duration}
@Author : INIC
******************/
  right() {
    gsap.to(this.sliderWrapperRef.current, {
      duration: 0.4,
      scrollTo: {
        x: "+=" + this.state.itemWidth,
      },
    });
  }

/*******************
@Purpose : Used for stop slider
@Parameter : {}
@Author : INIC
******************/
  stop() {
    gsap.to(this.sliderWrapperRef.current, {
      duration: this.state.timeoffset,
      scrollTo: {
        x: "+=0",
      },
    });
  }

/*******************
@Purpose : Used for navigation buttons
@Parameter : {}
@Author : INIC
******************/
  setUpNavButtons() {
    const showLeftButton = this.sliderWrapperRef.current.scrollLeft > 0;
    const showRightButton =
      this.sliderWrapperRef.current.scrollLeft <
      this.sliderInnerRef.current.offsetWidth -
      this.sliderWrapperRef.current.offsetWidth -
      this.state.transformWidth;

    this.setState({
      ...this.state,
      showLeftButton,
      showRightButton,
    });
  }

/*******************
@Purpose : Used for add Event listner
@Parameter : {}
@Author : INIC
******************/
  componentDidMount() {
    this.updateCalculation();
    window.addEventListener('resize', this.updateCalculation);
  }

/*******************
@Purpose : Used for remove add Event listner
@Parameter : {}
@Author : INIC
******************/
  componentWillUnmount() {
    window.removeEventListener('resize', this.updateCalculation);
  }

/*******************
@Purpose : Used for update calculations
@Parameter : {}
@Author : INIC
******************/
  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.updateCalculation();
    }
  }

/*******************
@Purpose : Used for update calculations
@Parameter : {}
@Author : INIC
******************/
  updateCalculation = () => {
    let noOfItemsToDivide = 2.5; // LG
    const windowWidth = window.outerWidth;
    const isMobile = windowWidth <= 761;
    if (isMobile) {
      noOfItemsToDivide = 1.1; // SM
    } else if (windowWidth > 1024 && windowWidth < 1640) {
      noOfItemsToDivide = 2.5;
    } else if (windowWidth > 761 && windowWidth < 1024) {
      noOfItemsToDivide = 1.5; // MD
    } else {
      noOfItemsToDivide = 2.5; // LG
    }
    const widthToManage = this.headerContainerRef.current.getBoundingClientRect().left + 15;
    const transformWidth = (window.innerWidth - this.containerRef.current.offsetWidth) / 2 + widthToManage;
    const wrapperWidth = this.sliderWrapperRef.current.offsetWidth;
    const itemWidth = isMobile
      ? wrapperWidth - transformWidth - 48
      : wrapperWidth / noOfItemsToDivide;

    const wasUnmeasured = !this.state.measured;
    this.setState(
      {
        ...this.state,
        transformWidth,
        contentWidth: wrapperWidth,
        itemWidth,
        imgHeight: itemWidth * (this.imgHeight / this.imgWidth),
        measured: true
      },
      () => {
        if (wasUnmeasured && this.sliderWrapperRef.current) {
          this.sliderWrapperRef.current.scrollLeft = 0;
        }
        this.setUpNavButtons();
      }
    );
  };
  /*******************
  @purpose : Rander HTML/ React Components
  @Author : INIC
  ******************/
  render() {
    return (
      <section className='testimonial py-75 mb-space-40'>
        <div className='custom-container' id="containerLeft1" ref={this.headerContainerRef}>
          <div className='section-heading'>
            <h3 className='text-dark testimonial-heading'>What Our Customers Are Saying</h3>
          </div>


        </div>
        <div className="slider-container custom-container" ref={this.containerRef} >
          <div
            className="slider-wrapper slider-overflow-auto"
            ref={this.sliderWrapperRef}
            onScroll={() => this.setUpNavButtons()}
            style={{
              scrollPaddingLeft: `${this.state.transformWidth}px`,
              scrollPaddingRight: `${this.state.transformWidth}px`,
            }}
          >
            <div className="slider-inner" ref={this.sliderInnerRef} style={{ width: this.state.itemWidth * this.props.data?.length + this.state.transformWidth * 2 + "px", visibility: this.state.measured ? 'visible' : 'hidden' }} >
              {this.state.measured && this.props.data &&
                this.props.data.map((row, index) => {
                  const isFirst = index === 0;
                  const isLast = index === this.props.data.length - 1;
                  return (
                    <div
                      className="slider-boxes"
                      key={index}
                      style={{
                        width: `${this.state.itemWidth}px`,
                        marginLeft: isFirst ? `${this.state.transformWidth}px` : undefined,
                        marginRight: isLast ? `${this.state.transformWidth}px` : undefined,
                      }}
                    >
                        <ReviewCard row={row} />
                    </div>
                  );
                })}
            </div>
          </div>

          <div
            className={
              this.state.showLeftButton
                ? "slider-action-btn action-prev"
                : "d-none"
            }
            onClick={() => this.left()}
          >
            <span className="icon icon-chevron-left"></span>
          </div>

          <div
            className={
              this.state.showRightButton
                ? "slider-action-btn action-next"
                : "d-none"
            }
            onClick={() => this.right()}
          >
            <span className="icon icon-chevron-right"></span>
          </div>
        </div>
      </section>
    );
  }
}

export default GoogleReviews;

